import { ReactNode } from "react"
import { Chip, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

const useStyles = makeStyles()((theme) => ({
    chip: {
        height: "auto",
        padding: ".5rem .875rem",
        borderRadius: "62.5rem",
        "& .MuiChip-icon": {
            margin: 0,
        },
        "& .MuiChip-label": {
            paddingLeft: ".5rem",
            paddingRight: 0,
        },
    },
    chipChecked: {
        borderColor: theme.palette.success.light,
        background: theme.palette.tailwind.emerald[50],
    },
    check: {
        width: "1.125rem",
        height: "1.125rem",
        borderRadius: ".3125rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        background: theme.palette.common.white,
        color: theme.palette.common.white,
    },
    checkChecked: {
        borderColor: theme.palette.success.light,
        background: theme.palette.success.light,
        // MUI colours the chip's icon slot itself, so the tick has to be told its colour directly
        "& svg": {
            color: theme.palette.common.white,
        },
    },
    count: {
        color: theme.palette.tailwind.slate[500],
    },
}))

type Props = Readonly<{
    isChecked: boolean
    onChange: (isChecked: boolean) => void
    /** Muted number after the label, e.g. how many items the option covers. */
    count?: number
    children: ReactNode
    className?: string
}>

/** A pill with a tick box for picking several options out of a set, e.g. product groups. */
export function StandardCheckChip({ isChecked, onChange, count, children, className }: Props) {
    const { classes, cx } = useStyles()

    return (
        <Chip
            variant="outlined"
            role="checkbox"
            aria-checked={isChecked}
            className={cx(classes.chip, isChecked && classes.chipChecked, className)}
            onClick={() => onChange(!isChecked)}
            icon={
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    className={cx(classes.check, isChecked && classes.checkChecked)}
                >
                    {isChecked && <FontAwesomeIcon icon={faCheck} size="xs" />}
                </Stack>
            }
            label={
                <Stack direction="row" spacing=".5rem" alignItems="center">
                    <span>{children}</span>
                    {count !== undefined && (
                        <Typography variant="caption" className={classes.count}>
                            {count}
                        </Typography>
                    )}
                </Stack>
            }
        />
    )
}
